import { LLMModel } from '../types/admin';
import { formatUnitPriceLabel } from '../components/admin/llm-catalog/modelPricing';
import { formatAdminNumber } from './numberFormat';

export function formatCatalogModelOption(model: LLMModel) {
  const { providerModel, priceLabel, taskTypeLabel } = getCatalogModelOptionParts(model);

  return `${providerModel} | ${priceLabel} | ${taskTypeLabel}`;
}

export function getCatalogModelOptionParts(model: LLMModel) {
  const provider = model.provider?.code ?? 'unknown';

  return {
    providerModel: `${provider}/${model.name || model.code}`,
    priceLabel: formatModelPriceLabel(model),
    taskTypeLabel: model.taskType?.name ?? model.taskTypeCode ?? '-',
    outputLabel: formatOutputModalities(model.outputModalities, model.taskType?.outputModalities),
  };
}

export function formatModelPriceLabel(model: LLMModel): string {
  const totalPrice = getTotalPricePerMillionTokens(model);
  if (totalPrice !== null) return `${formatAdminNumber(totalPrice)}$/1M`;

  const unitPriceLabel =
    model.prices
      ?.filter((price) => price.isActive !== false)
      .map((price) => formatUnitPriceLabel(price))
      .find((label): label is string => Boolean(label)) ?? null;

  return unitPriceLabel ?? '-';
}

export function getTotalPricePerMillionTokens(model: LLMModel) {
  if (model.totalPricePerMillionTokens !== undefined && model.totalPricePerMillionTokens !== null) {
    return model.totalPricePerMillionTokens;
  }

  const prices = [
    model.inputPricePerMillionTokens,
    model.cachePricePerMillionTokens,
    model.cacheWritePricePerMillionTokens,
    model.outputPricePerMillionTokens,
  ].filter((value): value is number => value !== undefined && value !== null);

  if (prices.length === 0) return null;

  return prices.reduce((sum, value) => sum + value, 0);
}

export function formatOutputModalities(value?: string | string[] | null, fallback?: string[]) {
  const modalities = parseModalities(value);
  if (modalities.length === 0 && fallback?.length) {
    return fallback.join(', ');
  }

  return modalities.length > 0 ? modalities.join(', ') : '-';
}

function parseModalities(value?: string | string[] | null) {
  if (!value) return [];

  const flatten = (source: unknown): string[] => {
    if (typeof source === 'string') {
      return source
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
    }

    if (Array.isArray(source)) {
      return source.flatMap(flatten);
    }

    return [];
  };

  if (Array.isArray(value)) {
    return Array.from(new Set(flatten(value)));
  }

  try {
    return Array.from(new Set(flatten(JSON.parse(value))));
  } catch {
    return Array.from(new Set(flatten(value)));
  }
}
